import React, { useState, useCallback } from "react";
import { Box, IconButton, makeStyles, Modal, Typography } from "@material-ui/core";
import Image from "next/image";
import ReactImageMagnify from "react-image-magnify";
import ClearIcon from "@material-ui/icons/Clear";
import { isProductNew } from "./utils";

function getModelPictureAlt(product, number) {
  return [product.product_type, product.subcategory1, product.brand, product.model_reference, number]
    .filter((x) => x)
    .join(" ");
}

const newShape = "new-bg";
const secondHandShape = "reco-bg";

function formatPhotoWithShape(src, maxWidth, addBrightness, isProductNew) {
  const shape = isProductNew ? newShape : secondHandShape;
  return src
    .replace(
      "https://res.cloudinary.com/cobble/image/upload",
      `w_${maxWidth || 600},h_${(maxWidth || 600) / 0.8}${
        addBrightness ? ",e_brightness:20" : ""
      },c_pad/u_ecommerce:assets:${shape},w_${maxWidth || 600},h_${(maxWidth || 600) / 0.8}/`
    )
    .replace(".png", ".jpg");
}

const formatCloudinaryPhoto = (src, maxWidth, hideShape, addUrlPrefix, addBrightness, isProductNew) => {
  const urlPrefix = addUrlPrefix ? "https://res.cloudinary.com/cobble/image/upload/" : "";
  if (src.startsWith("https://res.cloudinary.com/")) {
    if (!hideShape) {
      src = formatPhotoWithShape(src, maxWidth, addBrightness, isProductNew);
    }
    src = src
      .replace(
        "https://res.cloudinary.com/cobble/image/upload",
        `c_fill,w_${maxWidth || 600},h_${(maxWidth || 600) / 0.8}${addBrightness ? ",e_brightness:20" : ""}${
          addBrightness ? ",b_rgb:CFCFCF" : ",b_rgb:F9F9F9"
        },c_pad/`
      )
      .replace(".png", ".jpg");
  }
  return urlPrefix + src;
};

export default function ProductImage({
  width = "100%",
  maxWidth,
  src,
  product,
  propagateError,
  placeholder,
  addBrightness,
  number = 1,
  zoomOnDesktop,
  zoomOnMobile,
  hideShape,
  offerTag,
  productStateTag,
}) {
  const classes = useStyle({ width });
  const [error, setError] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);

  const handleClose = () => {
    setIsModalOpen(false);
  };

  const pictureAlt = product ? getModelPictureAlt(product, number) : null;

  const isNew = isProductNew(product);

  const handleError = useCallback(() => {
    setError(true);
    if (propagateError) {
      propagateError(src);
    }
  }, [propagateError, src]);
  if (src && !error) {
    return (
      <div className={classes.container}>
        {zoomOnDesktop ? (
          <>
            <ReactImageMagnify
              {...{
                smallImage: {
                  alt: pictureAlt,
                  isFluidWidth: true,
                  src: formatCloudinaryPhoto(src, maxWidth, hideShape, true, addBrightness, isNew),
                  key: src,
                  width: maxWidth,
                  onError: handleError,
                },
                largeImage: {
                  src: formatCloudinaryPhoto(src, 1800, hideShape, true, addBrightness, isNew),
                  width: 1800,
                  height: 2400,
                  onError: handleError,
                },
              }}
            />
            <Box className={classes.tags}>
              {productStateTag}
              {offerTag}
            </Box>
          </>
        ) : (
          <Box>
            {!isModalOpen && (
              <Box>
                {zoomOnMobile && (
                  <Box className={classes.zoomText} onClick={() => zoomOnMobile && setIsModalOpen(true)}>
                    Clic pour zoomer
                  </Box>
                )}
                <Image
                  width={400}
                  height={538}
                  onClick={() => zoomOnMobile && setIsModalOpen(true)}
                  src={formatCloudinaryPhoto(src, maxWidth, hideShape, false, addBrightness, isNew)}
                  key={src}
                  layout="intrinsic"
                  alt={pictureAlt}
                  className={classes.image}
                  onError={handleError}
                  priority={true}
                />
                <Box className={classes.tags}>
                  {productStateTag}
                  {offerTag}
                </Box>
              </Box>
            )}
            <Modal open={isModalOpen} onClose={handleClose}>
              <Box width={"100vw"} height={"80vh"} overflow={"scroll"}>
                <IconButton size="small" onClick={() => handleClose()} className={classes.icon}>
                  <ClearIcon />
                </IconButton>
                <Box>
                  <Box width={900} height={1200}>
                    <Image
                      src={formatCloudinaryPhoto(src, 1800, hideShape, false, addBrightness, isNew)}
                      key={src}
                      width={1800}
                      height={2400}
                      layout="responsive"
                      alt={pictureAlt}
                      className={classes.image}
                      onError={handleError}
                    />
                  </Box>
                </Box>
              </Box>
            </Modal>
          </Box>
        )}
      </div>
    );
  }
  if (placeholder !== undefined) {
    return placeholder;
  }
  return (
    <div className={`${classes.container} ${classes.containerPlaceholder}`}>
      <div className={classes.placeholder}>
        <Typography align="center" variant="caption">
          Pas de photo
        </Typography>
      </div>
    </div>
  );
}

const useStyle = makeStyles(() => ({
  container: {
    position: "relative",
    width: (props) => props.width,
    aspectRatio: "4/5",
  },
  image: {
    color: "transparent",
    width: "100%",
    height: "100%",
  },
  placeholder: {
    position: "absolute",
    backgroundColor: "#FAFAFA",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    top: 0,
    bottom: 0,
    right: 0,
    left: 0,
  },
  icon: {
    position: "absolute",
    zIndex: 100,
    right: 12,
    top: 12,
    background: "white",
  },
  zoomText: {
    position: "absolute",
    background: "white",
    opacity: 0.9,
    zIndex: 4,
    padding: 12,
    border: "1px solid #D4D4D4",
  },
  tags: {
    display: "flex",
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    position: "absolute",
    left: 0,
    bottom: 8,
    marginLeft: 16,
    marginRight: 16,
    width: "90%",
  },
}));
